<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('changePassword') }}
    </h1>

    <div class="w-full flex justify-center">
      <div class="rounded-md border border-brand-gray-300 p-2 w-full md:w-1/3">
        <Form
          class="flex flex-col gap-3"
          v-slot="{ values, resetForm }"
          @submit="(values) => onSubmit(values, resetForm)"
        >
          <div class="flex gap-4 flex-col">
            <Field
              :rules="requiredRule"
              validate-on-input
              validate-on-change
              validate-on-blur
              name="oldPassword"
              v-slot="{ field, errors: errs }"
            >
              <TextInput
                :field="field"
                :errors="errs"
                :label="$t('OldPassword')"
                input-type="password"
                label-class="w-full"
                class="w-full"
                direction="flex-col"
              />
            </Field>
            <Field
              :rules="strongPassword"
              validate-on-input
              validate-on-change
              validate-on-blur
              name="newPassword"
              v-slot="{ field, errors: errs }"
            >
              <TextInput
                :field="field"
                :errors="errs"
                :label="$t('newPassword')"
                input-type="password"
                label-class="w-full"
                class="w-full"
                direction="flex-col"
              />
            </Field>
            <Field
              :rules="() => equalPassword(values.newPassword, values.confirmPassword)"
              validate-on-input
              validate-on-change
              validate-on-blur
              name="confirmPassword"
              v-slot="{ field, errors: errs }"
            >
              <TextInput
                :field="field"
                :errors="errs"
                :label="i18n.t('confirmPassword')"
                input-type="password"
                label-class="w-full"
                class="w-full"
                direction="flex-col"
              />
            </Field>
          </div>
          <button
            class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faDiskette class="w-4 fill-white"></faDiskette>
            </div>
            <span class="w-full text-center">
              {{ $t('Confirm') }}
            </span>
            <div class="p-0.5"></div>
          </button>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import authService from '@/services/auth'
import { notify } from 'notiwind'
import { Field, Form } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const router = useRouter()
const i18n = useI18n({
  locale: 'en',
  messages: {
    en: {
      confirmPassword: 'Confirm New Password'
    },
    ar: {
      confirmPassword: 'تأكيد كلمة المرور الجديدة'
    }
  }
})

//#region rules
function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function strongPassword(value) {
  const requiredResult = requiredRule(value)
  if (requiredResult !== true) {
    return requiredResult
  }

  const isMinCharPassed = value.trim().length >= 8
  const includesUpper = /[A-Z]/.test(value) // Checks for at least one uppercase letter
  const includesLower = /[a-z]/.test(value) // Checks for at least one lowercase letter
  const includesNumbers = /[0-9]/.test(value) // Checks for at least one number
  const includesSpecialCharacters = /[!@#$%^&*(),.?":{}|<>]/.test(value) // Checks for at least one special character

  const isStrong = [
    isMinCharPassed,
    includesUpper,
    includesLower,
    includesNumbers,
    includesSpecialCharacters
  ].every((e) => !!e)

  if (!isStrong) {
    return i18n.t('PasswordPattern')
  }

  return true
}

function equalPassword(oldPassword, newPassword) {
  const requiredResult = requiredRule(newPassword)
  if (requiredResult !== true) {
    return requiredResult
  }

  if (oldPassword != newPassword) {
    return i18n.t('PasswordNotEqual')
  }

  return true
}
//#endregion

const onSubmit = async (values, resetForm) => {
  const { newPassword, oldPassword } = values
  try {
    await authService.changePassword({ newPassword, oldPassword })
    notify(
      {
        group: 'success',
        title: i18n.t('changeSuccess')
      },
      6000
    )

    resetForm()
    router.push('/')
  } catch (error) {
    notify(
      {
        group: 'error',
        title: i18n.t('errorSetDefaultAddress'),
        text: i18n.t(error.toString())
      },
      6000
    )
  }
}
</script>

<style lang="scss" scoped></style>
